import { Circle } from "react-leaflet";
import { useAsset } from "../useAsset";
import { Annotation } from "../../../model/internal/annotation";
import { LatLng } from "leaflet";
import L from "leaflet";
import { useDispatch } from "react-redux";
import { BLUE_HIGHLIGHT, DEFAULT_ANNOTATION_COLOR } from "../../../constants/colors";
import { ShapeLineWidth } from "../../../constants/annotation";
import ResizeAndTranslateTool from "../../draw-tools/ResizeAndTranslateTool";
import { updateShapeAnnotationDirectly } from "../../../app/networkSlice";
import "leaflet-path-drag";

interface CircleAnnotationProps {
  circle: Annotation;
  highlighted: boolean;
  arrowSnappingActive: boolean;
  easyClickTolerance: number;
  eventHandlers?: L.LeafletEventHandlerFnMap;
}

export const CircleAnnotation = ({
  circle,
  highlighted,
  arrowSnappingActive,
  easyClickTolerance,
  eventHandlers,
}: CircleAnnotationProps) => {
  const dispatch = useDispatch();
  const { isAssetSelected, isAssetRingfenced } = useAsset();
  const isSelected = isAssetSelected(circle.id);
  const isRingfenced = isAssetRingfenced(circle.id);

  // Calculate rectangle corners from circle center and radius
  const getRectangleCorners = () => {
    if (!circle.geometry.center || !circle.geometry.radius) return [];
    const lat = circle.geometry.center.lat;
    const lng = circle.geometry.center.lng;
    // conversion: 1 deg lat ≈ 111320m, 1 deg lng ≈ 40075000*cos(lat)/360 m
    const dLat = circle.geometry.radius / 111320;
    const dLng = circle.geometry.radius / ((40075000 * Math.cos((lat * Math.PI) / 180)) / 360);
    return [
      [lat + dLat, lng - dLng], // NW
      [lat + dLat, lng + dLng], // NE
      [lat - dLat, lng + dLng], // SE
      [lat - dLat, lng - dLng], // SW
    ];
  };

  return (
    <>
      <Circle
        key={`${circle.id}-circle-annotation`}
        center={circle.geometry.center}
        radius={circle.geometry.radius}
        pathOptions={{
          ...circle.styles,
          color:
            highlighted || isRingfenced
              ? BLUE_HIGHLIGHT
              : circle.styles.color || DEFAULT_ANNOTATION_COLOR,
          weight: ShapeLineWidth,
          fill: highlighted || arrowSnappingActive,
          fillOpacity: 0.1,
          opacity: isRingfenced ? 0.5 : 1,
        }}
        eventHandlers={eventHandlers}
      />
      <Circle
        key={`${circle.id}-circle-selector`}
        center={circle.geometry.center}
        radius={circle.geometry.radius}
        pathOptions={{
          ...circle.styles,
          color: "black",
          weight: ShapeLineWidth + 2 * easyClickTolerance,
          fill: false,
          fillOpacity: 0,
          opacity: 0,
        }}
        eventHandlers={eventHandlers}
      />
      {isSelected && (
        <ResizeAndTranslateTool
          id={circle.id}
          geometry={getRectangleCorners() as unknown as LatLng[]}
          rotation={false}
          uniformScaling={true}
          onTransform={(newGeometry) => {
            const latlngs = newGeometry as L.LatLng[];

            const width = latlngs[0].distanceTo(latlngs[1]);
            const height = latlngs[1].distanceTo(latlngs[2]);
            const newRadius = Math.max(width, height) / 2;

            const centerLat =
              (latlngs[0].lat + latlngs[1].lat + latlngs[2].lat + latlngs[3].lat) / 4;
            const centerLng =
              (latlngs[0].lng + latlngs[1].lng + latlngs[2].lng + latlngs[3].lng) / 4;
            const centroid = { lat: centerLat, lng: centerLng };

            dispatch(
              updateShapeAnnotationDirectly({
                ...circle,
                geometry: {
                  center: centroid,
                  radius: newRadius,
                },
              }),
            );
          }}
        />
      )}
    </>
  );
};
